/**
 * Enhanced tenant utilities for Railway deployment
 * Handles Railway's specific subdomain structure
 */

export interface TenantInfo {
  subdomain: string;
  isDefault: boolean;
  isMainDomain: boolean;
  hostname: string;
  baseDomain: string;
}

/**
 * Get base domain from environment or detect from URL
 */
const getBaseDomain = (): string => {
  // Use environment variable if available
  if (process.env.REACT_APP_BASE_DOMAIN) {
    return process.env.REACT_APP_BASE_DOMAIN;
  }
  
  if (typeof window === 'undefined') {
    return 'jarvisrcsa.up.railway.app';
  }
  
  const hostname = window.location.hostname;
  
  // Handle Railway domain
  if (hostname.endsWith('.railway.app')) {
    // Extract base domain from Railway URL
    const match = hostname.match(/([^.]+\.up\.railway\.app)$/);
    return match ? match[1] : 'jarvisrcsa.up.railway.app';
  }
  
  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }
  
  // For other domains, extract base domain
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return parts.slice(-2).join('.');
  }
  
  return hostname;
};

/**
 * Extract tenant information from current URL (Railway-aware)
 */
export const getTenantFromUrl = (): TenantInfo => {
  if (typeof window === 'undefined') {
    return { 
      subdomain: 'default', 
      isDefault: true, 
      isMainDomain: true,
      hostname: '',
      baseDomain: 'jarvisrcsa.up.railway.app'
    };
  }

  const hostname = window.location.hostname;
  const baseDomain = getBaseDomain();
  
  // Handle Railway deployment
  if (hostname.endsWith('.railway.app')) {
    // Check if it's the main Railway app URL
    if (hostname === baseDomain) {
      return {
        subdomain: 'default',
        isDefault: true,
        isMainDomain: true,
        hostname,
        baseDomain
      };
    }
    
    // Extract subdomain from Railway URL
    // Format: subdomain.jarvisrcsa.up.railway.app
    const subdomainMatch = hostname.match(/^([^.]+)\.(.+\.up\.railway\.app)$/);
    
    if (subdomainMatch && subdomainMatch[1]) {
      const subdomain = subdomainMatch[1];
      const extractedBaseDomain = subdomainMatch[2];
      
      return {
        subdomain,
        isDefault: subdomain === 'default',
        isMainDomain: false,
        hostname,
        baseDomain: extractedBaseDomain
      };
    }
  }
  
  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    if (hostname.includes('.localhost')) {
      const parts = hostname.split('.');
      if (parts.length >= 2 && parts[0] !== 'www') {
        return {
          subdomain: parts[0],
          isDefault: false,
          isMainDomain: false,
          hostname,
          baseDomain: 'localhost'
        };
      }
    }
    
    // Check localStorage for development subdomain
    const storedSubdomain = localStorage.getItem('dev-tenant-subdomain');
    
    if (!storedSubdomain || storedSubdomain === 'main' || storedSubdomain === 'default') {
      return {
        subdomain: 'default',
        isDefault: true,
        isMainDomain: true,
        hostname,
        baseDomain: 'localhost'
      };
    }
    
    return {
      subdomain: storedSubdomain,
      isDefault: storedSubdomain === 'default',
      isMainDomain: false,
      hostname,
      baseDomain: 'localhost'
    };
  }
  
  // Handle custom domains
  const parts = hostname.split('.');
  
  if (parts.length >= 3) {
    const subdomain = parts[0];
    
    if (['www', 'api', 'admin', 'app', 'mail', 'ftp'].includes(subdomain)) {
      return { 
        subdomain: 'default', 
        isDefault: true,
        isMainDomain: true,
        hostname,
        baseDomain
      };
    }
    
    return { 
      subdomain, 
      isDefault: subdomain === 'default',
      isMainDomain: false,
      hostname,
      baseDomain
    };
  }
  
  // Main domain (no subdomain)
  return { 
    subdomain: 'default', 
    isDefault: true,
    isMainDomain: true,
    hostname,
    baseDomain
  };
};

/**
 * Build tenant-specific URL for Railway deployment
 */
export const buildTenantUrl = (path: string = '', subdomain?: string): string => {
  if (typeof window === 'undefined') {
    return path;
  }

  const currentTenant = getTenantFromUrl();
  const targetSubdomain = subdomain || currentTenant.subdomain;
  const protocol = window.location.protocol;
  const port = window.location.port;
  const baseDomain = currentTenant.baseDomain;

  // Handle localhost development
  if (baseDomain === 'localhost' || baseDomain === '127.0.0.1') {
    const portSuffix = port ? `:${port}` : '';
    
    // For localhost, use subdomain.localhost pattern
    if (targetSubdomain && targetSubdomain !== 'main' && targetSubdomain !== 'default') {
      return `${protocol}//${targetSubdomain}.localhost${portSuffix}${path}`;
    }
    
    return `${protocol}//localhost${portSuffix}${path}`;
  }

  // Handle Railway deployment
  if (baseDomain.endsWith('.railway.app')) {
    // For main domain
    if (targetSubdomain === 'main' || targetSubdomain === 'default' || !targetSubdomain) {
      return `${protocol}//${baseDomain}${path}`;
    }
    
    // For subdomains
    return `${protocol}//${targetSubdomain}.${baseDomain}${path}`;
  }

  // Handle custom domains
  if (targetSubdomain === 'main' || targetSubdomain === 'default' || !targetSubdomain) {
    return `${protocol}//${baseDomain}${path}`;
  }
  
  return `${protocol}//${targetSubdomain}.${baseDomain}${path}`;
};

/**
 * Get API headers for tenant context
 */
export const getTenantHeaders = (): Record<string, string> => {
  const tenant = getTenantFromUrl();
  const headers: Record<string, string> = {};

  // Only send tenant header for non-main domains
  if (!tenant.isMainDomain && tenant.subdomain !== 'main' && tenant.subdomain !== 'default') {
    headers['X-Tenant-Subdomain'] = tenant.subdomain;
  }

  // Commented out to avoid CORS issues - uncomment when backend is updated
  // headers['X-Base-Domain'] = tenant.baseDomain;

  return headers;
};

/**
 * Redirect to tenant-specific URL (Railway-aware)
 */
export const redirectToTenant = (subdomain: string, path: string = ''): void => {
  if (typeof window !== 'undefined') {
    const url = buildTenantUrl(path, subdomain);
    window.location.href = url;
  }
};

/**
 * Check if we're in Railway deployment
 */
export const isRailwayDeployment = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  return window.location.hostname.endsWith('.railway.app');
};

/**
 * Get tenant-aware API URL
 */
export const getTenantApiUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';

  // In production, always use the main API URL
  if (isRailwayDeployment()) {
    return apiUrl;
  }

  // In development, use localhost
  return apiUrl;
};

/**
 * Tenant-aware localStorage with proper isolation
 */
export const getTenantStorageKey = (key: string): string => {
  const tenant = getTenantFromUrl();
  
  // Use subdomain for isolation, but handle main domain specially
  const tenantKey = tenant.isMainDomain ? 'default' : tenant.subdomain;
  return `tenant:${tenantKey}:${key}`;
};

export const getTenantLocalStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const tenantKey = getTenantStorageKey(key);
  return localStorage.getItem(tenantKey);
};

export const setTenantLocalStorageItem = (key: string, value: string): void => {
  if (typeof window !== 'undefined') {
    const tenantKey = getTenantStorageKey(key);
    localStorage.setItem(tenantKey, value);
  }
};

export const removeTenantLocalStorageItem = (key: string): void => {
  if (typeof window !== 'undefined') {
    const tenantKey = getTenantStorageKey(key);
    localStorage.removeItem(tenantKey);
  }
};

/**
 * Clear all tenant-specific localStorage items
 */
export const clearTenantStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const tenant = getTenantFromUrl();
  const tenantPrefix = `tenant:${tenant.isMainDomain ? 'default' : tenant.subdomain}:`;
  
  // Get all keys and remove tenant-specific ones
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(tenantPrefix)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Set tenant subdomain for development
 */
export const setDevTenantSubdomain = (subdomain: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('dev-tenant-subdomain', subdomain);
    // Reload to apply the new tenant context
    window.location.reload();
  }
};

/**
 * Check if we're in development mode
 */
export const isDevelopment = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
};

/**
 * Check if subdomain is valid
 */
export const validateSubdomain = (subdomain: string): boolean => {
  // Basic subdomain validation
  const subdomainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,48}[a-zA-Z0-9])?$/;
  return subdomainRegex.test(subdomain);
};